// Helpers for card invitations: event formatting, calendar links, RSVP summary export.

export type RsvpResponse = 'yes' | 'maybe' | 'no';

export type CardEvent = {
	enabled: boolean;
	title: string;
	startsAt: string;
	endsAt?: string;
	timezone: string;
	locationType: 'in_person' | 'online';
	address?: string;
	onlineUrl?: string;
	hostNote?: string;
	allowGuests: boolean;
};

export type SummaryRow = {
	recipientId: string;
	name: string;
	email: string;
	response: RsvpResponse | 'none';
	guests: number;
	note: string;
	updatedAt: string | null;
};

export type RsvpSummary = {
	event: CardEvent;
	counts: { yes: number; maybe: number; no: number; none: number; guests: number };
	rows: SummaryRow[];
};

const HOUR_MS = 60 * 60 * 1000;

const endOf = (event: CardEvent): Date =>
	event.endsAt ? new Date(event.endsAt) : new Date(new Date(event.startsAt).getTime() + HOUR_MS);

/** UTC basic format used by iCalendar and Google Calendar: YYYYMMDDTHHMMSSZ. */
const toUtcStamp = (d: Date): string =>
	d
		.toISOString()
		.replace(/[-:]/g, '')
		.replace(/\.\d{3}/, '');

/** Date and time in the event's own timezone, followed by the short timezone name. */
export const formatEventWhen = (event: CardEvent, locale: string): string => {
	const opts: Intl.DateTimeFormatOptions = {
		dateStyle: 'full',
		timeStyle: 'short',
		timeZone: event.timezone,
	};
	const start = new Date(event.startsAt);
	const base = new Intl.DateTimeFormat(locale, opts).format(start);
	const tz =
		new Intl.DateTimeFormat(locale, { timeZone: event.timezone, timeZoneName: 'short' })
			.formatToParts(start)
			.find((p) => p.type === 'timeZoneName')?.value || event.timezone;
	return `${base} ${tz}`;
};

export const mapsUrl = (address: string): string =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export const googleCalendarUrl = (event: CardEvent, detailsUrl?: string): string => {
	const params = new URLSearchParams({
		action: 'TEMPLATE',
		text: event.title,
		dates: `${toUtcStamp(new Date(event.startsAt))}/${toUtcStamp(endOf(event))}`,
	});
	const location = event.locationType === 'online' ? event.onlineUrl : event.address;
	if (location) params.set('location', location);
	const details = [event.hostNote, detailsUrl].filter(Boolean).join('\n\n');
	if (details) params.set('details', details);
	return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const icsEscape = (s: string): string => s.replace(/\\/g, '\\\\').replace(/[,;]/g, '\\$&').replace(/\r?\n/g, '\\n');

export const buildIcs = (event: CardEvent, uid: string): string => {
	const location = event.locationType === 'online' ? event.onlineUrl : event.address;
	const lines = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//CardClan//Invitation//EN',
		'CALSCALE:GREGORIAN',
		'BEGIN:VEVENT',
		`UID:${uid}`,
		`DTSTAMP:${toUtcStamp(new Date())}`,
		`DTSTART:${toUtcStamp(new Date(event.startsAt))}`,
		`DTEND:${toUtcStamp(endOf(event))}`,
		`SUMMARY:${icsEscape(event.title)}`,
	];
	if (location) lines.push(`LOCATION:${icsEscape(location)}`);
	if (event.hostNote) lines.push(`DESCRIPTION:${icsEscape(event.hostNote)}`);
	lines.push('END:VEVENT', 'END:VCALENDAR');
	return lines.join('\r\n') + '\r\n';
};

const csvCell = (value: string | number | null | undefined): string => {
	let s = value === null || value === undefined ? '' : String(value);
	if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
	return `"${s.replace(/"/g, '""')}"`;
};

export const rsvpRowsToCsv = (rows: SummaryRow[]): string => {
	const header = 'Name,Email,Response,Guests,Note,Updated';
	const body = rows.map((r) => [r.name, r.email, r.response, r.guests, r.note, r.updatedAt].map(csvCell).join(','));
	return [header, ...body].join('\n');
};

/** Offset (ms) of `timeZone` from UTC at the instant `date`: wall-clock-as-UTC minus the real instant. */
const zoneOffsetMs = (date: Date, timeZone: string): number => {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).formatToParts(date);
	const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
	const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
	return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

/** Interprets a datetime-local value ('YYYY-MM-DDTHH:mm') as wall-clock time in `timeZone`; returns a UTC ISO string. */
export const zonedLocalToIso = (local: string, timeZone: string): string => {
	const [, y, mo, d, h, mi] = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local) || [];
	const wall = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
	// Two passes so the offset is taken at the resulting instant (handles DST boundaries).
	let utc = wall - zoneOffsetMs(new Date(wall), timeZone);
	utc = wall - zoneOffsetMs(new Date(utc), timeZone);
	return new Date(utc).toISOString();
};

/** Formats a UTC ISO instant as a datetime-local value ('YYYY-MM-DDTHH:mm') in `timeZone`. */
export const isoToZonedLocal = (iso: string, timeZone: string): string => {
	const date = new Date(iso);
	return new Date(date.getTime() + zoneOffsetMs(date, timeZone)).toISOString().slice(0, 16);
};

/** Download filename for the RSVP export: sanitized card title, falling back to 'card'. */
export const rsvpCsvFilename = (cardTitle?: string | null): string =>
	`${(cardTitle || '').replace(/[^\w\- ]+/g, '').trim() || 'card'}-rsvps.csv`;
